<template>
  <div class="mrts">
    <div class="mrts__container">
      <!-- Header -->
      <div class="mrts__header">
        <div>
          <span class="mrts__eyebrow">ตั้งค่า / รายงานประจำเดือน</span>
          <h1 class="mrts__title">
            Monthly Reports
          </h1>
          <p class="mrts__subtitle">
            แต่ละรายงานจะดึงสินค้าตามแท็กที่กำหนด แล้วส่งอีเมลสรุปอัตโนมัติทุกวันที่ 7 ของเดือน
          </p>
        </div>
        <button
          type="button"
          class="mrts__btn mrts__btn--primary"
          @click="openCreate">
          + สร้างรายงานใหม่
        </button>
      </div>

      <!-- Stat cards -->
      <div class="mrts__stats">
        <div class="mrts__stat">
          <div class="mrts__stat-label">
            รายงานทั้งหมด
          </div>
          <div class="mrts__stat-value">
            {{ stats.total }}
          </div>
        </div>
        <div class="mrts__stat">
          <div class="mrts__stat-label">
            กำลังใช้งาน
          </div>
          <div class="mrts__stat-value mrts__stat-value--green">
            {{ stats.active }}
          </div>
        </div>
        <div class="mrts__stat">
          <div class="mrts__stat-label">
            แท็กที่ใช้
          </div>
          <div class="mrts__stat-value">
            {{ stats.tags }}
          </div>
        </div>
        <div class="mrts__stat">
          <div class="mrts__stat-label">
            ส่งรอบถัดไป
          </div>
          <div class="mrts__stat-value mrts__stat-value--date">
            {{ nextSendLabel }}
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="mrts__table">
        <div
          v-if="!loading && total"
          class="mrts__pager mrts__pager--top">
          <div class="mrts__pager-rows">
            <span>Rows per page:</span>
            <select
              v-model.number="query.limit"
              class="mrts__pager-select"
              @change="onLimitChange">
              <option
                v-for="option in limitOptions"
                :key="option"
                :value="option">
                {{ option }}
              </option>
            </select>
          </div>
          <div class="mrts__pager-range">
            {{ rangeStart }}-{{ rangeEnd }} of {{ total }}
          </div>
          <div class="mrts__pager-nav">
            <button
              type="button"
              aria-label="ก่อนหน้า"
              class="mrts__pager-btn"
              :disabled="query.page <= 1"
              @click="prevPage">
              ‹
            </button>
            <button
              type="button"
              aria-label="ถัดไป"
              class="mrts__pager-btn"
              :disabled="query.page >= totalPages"
              @click="nextPage">
              ›
            </button>
          </div>
        </div>

        <div class="mrts__row mrts__row--head">
          <div>ชื่อรายงาน</div>
          <div>Tags สินค้า</div>
          <div>สถานะ</div>
          <div class="mrts__col-right">
            จัดการ
          </div>
        </div>

        <div
          v-if="loading"
          class="mrts__state">
          <v-progress-circular
            indeterminate
            color="#1F5F4B"
            size="28" />
        </div>

        <template v-else>
          <div
            v-for="report in reports"
            :key="reportId(report)"
            class="mrts__row mrts__row--item">
            <div>
              <div class="mrts__name">
                {{ report.name }}
              </div>
              <div class="mrts__meta">
                แก้ไขล่าสุด {{ formatThaiDate(report.updatedAt) }}
              </div>
            </div>

            <div class="mrts__chips">
              <span
                v-for="tag in visibleTags(report)"
                :key="tag"
                class="mrts__chip">
                {{ tag }}
              </span>
              <span
                v-if="moreCount(report)"
                class="mrts__chip mrts__chip--more">
                +{{ moreCount(report) }}
              </span>
            </div>

            <div>
              <button
                type="button"
                role="switch"
                :aria-checked="String(isActive(report))"
                title="เปิด / หยุดการส่งรายงาน"
                class="mrts__switch"
                :class="{ 'mrts__switch--on': isActive(report) }"
                :disabled="mutatingId === reportId(report)"
                @click="toggle(report)">
                <span class="mrts__switch-track">
                  <span class="mrts__switch-knob" />
                </span>
                {{ isActive(report) ? 'กำลังใช้งาน' : 'หยุดชั่วคราว' }}
              </button>
            </div>

            <div class="mrts__actions">
              <button
                type="button"
                class="mrts__btn mrts__btn--ghost"
                @click="openEdit(report)">
                แก้ไข
              </button>
              <button
                type="button"
                aria-label="ลบรายงาน"
                title="ลบรายงาน"
                class="mrts__icon-btn"
                @click="askDelete(report)">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round">
                  <path d="M2.6 4h10.8M6.4 4V2.6h3.2V4M4 4l.6 9.4h6.8L12 4M6.6 6.6v4.6M9.4 6.6v4.6" />
                </svg>
              </button>
            </div>

            <div
              v-if="confirmingId === reportId(report)"
              class="mrts__confirm">
              <span class="mrts__confirm-text">
                ลบ “{{ report.name }}” และหยุดส่งรายงานนี้ถาวร?
              </span>
              <div class="mrts__confirm-actions">
                <button
                  type="button"
                  class="mrts__btn mrts__btn--muted"
                  @click="cancelDelete">
                  ยกเลิก
                </button>
                <button
                  type="button"
                  class="mrts__btn mrts__btn--danger"
                  :disabled="mutatingId === reportId(report)"
                  @click="confirmDelete(report)">
                  ลบรายงาน
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="!total"
            class="mrts__empty">
            <div class="mrts__empty-title">
              ยังไม่มีรายงาน
            </div>
            <p class="mrts__empty-text">
              สร้างรายงานแรกเพื่อเริ่มส่งสรุปสินค้าประจำเดือน
            </p>
            <button
              type="button"
              class="mrts__btn mrts__btn--primary"
              @click="openCreate">
              + สร้างรายงานใหม่
            </button>
          </div>

          <div
            v-if="total"
            class="mrts__pager mrts__pager--bottom">
            <div class="mrts__pager-rows">
              <span>Rows per page:</span>
              <select
                v-model.number="query.limit"
                class="mrts__pager-select"
                @change="onLimitChange">
                <option
                  v-for="option in limitOptions"
                  :key="option"
                  :value="option">
                  {{ option }}
                </option>
              </select>
            </div>
            <div class="mrts__pager-range">
              {{ rangeStart }}-{{ rangeEnd }} of {{ total }}
            </div>
            <div class="mrts__pager-nav">
              <button
                type="button"
                aria-label="ก่อนหน้า"
                class="mrts__pager-btn"
                :disabled="query.page <= 1"
                @click="prevPage">
                ‹
              </button>
              <button
                type="button"
                aria-label="ถัดไป"
                class="mrts__pager-btn"
                :disabled="query.page >= totalPages"
                @click="nextPage">
                ›
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- Toast -->
      <div
        v-if="toast"
        class="mrts__toast">
        <span class="mrts__toast-dot" />{{ toast }}
      </div>
    </div>

    <!-- Modal -->
    <div
      v-if="modalOpen"
      class="mrts__veil">
      <div
        class="mrts__sheet"
        role="dialog"
        aria-modal="true">
        <div class="mrts__sheet-head">
          <div>
            <h2 class="mrts__sheet-title">
              {{ isEditing ? 'แก้ไขรายงาน' : 'สร้างรายงานใหม่' }}
            </h2>
            <p class="mrts__sheet-sub">
              กำหนดชื่อรายงานและแท็กสินค้าที่ต้องการรวมไว้
            </p>
          </div>
          <button
            type="button"
            aria-label="ปิด"
            class="mrts__close"
            @click="close">
            ×
          </button>
        </div>

        <div class="mrts__field">
          <label
            for="report-name"
            class="mrts__label">ชื่อรายงาน</label>
          <p class="mrts__hint">
            จะแสดงเป็นหัวข้ออีเมลและชื่อไฟล์ที่แนบไป
          </p>
          <input
            id="report-name"
            v-model="name"
            type="text"
            maxlength="60"
            placeholder="เช่น รายงานยอดขายกลุ่มสินค้าพรีเมียม"
            class="mrts__input"
            @input="onNameInput">
          <div class="mrts__counter">
            {{ name.length }}/60
          </div>
        </div>

        <div class="mrts__field mrts__field--tags">
          <label
            for="tag-input"
            class="mrts__label">Tags สินค้า</label>
          <p class="mrts__hint">
            เลือกจากแท็กสินค้า หรือพิมพ์แล้วกด Enter / , เพื่อเพิ่มแท็ก — เพิ่มได้หลายค่า
          </p>

          <div class="mrts__tagfield">
            <div
              class="mrts__tagbox"
              @click="focusInput">
              <span
                v-for="(tag, index) in tags"
                :key="`${tag}-${index}`"
                class="mrts__tag">
                {{ tag }}
                <button
                  type="button"
                  aria-label="ลบแท็ก"
                  class="mrts__tag-remove"
                  @click.stop="removeTag(index)">
                  ×
                </button>
              </span>
              <input
                id="tag-input"
                ref="tagInput"
                v-model="draft"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="String(dropdownVisible)"
                :placeholder="tags.length ? 'เพิ่มแท็ก…' : 'เลือกหรือพิมพ์แท็กสินค้า'"
                class="mrts__tag-input"
                @input="onDraftInput"
                @focus="openDropdown"
                @keydown="onKeyDown"
                @blur="closeDropdown">
            </div>

            <div
              v-if="dropdownVisible"
              class="mrts__dropdown"
              role="listbox">
              <button
                v-for="(option, index) in tagOptions"
                :key="option"
                type="button"
                role="option"
                :aria-selected="String(index === highlightIndex)"
                class="mrts__option"
                :class="{ 'mrts__option--active': index === highlightIndex }"
                @mousedown.prevent="selectOption(option)"
                @mousemove="highlightIndex = index">
                <span class="mrts__option-dot" />
                {{ option }}
              </button>
              <button
                v-if="canAddCustom"
                type="button"
                class="mrts__option mrts__option--add"
                @mousedown.prevent="selectOption(draft)">
                + เพิ่ม “{{ draft.trim() }}” เป็นแท็กใหม่
              </button>
            </div>
          </div>

          <div class="mrts__error">
            {{ error }}
          </div>

          <div
            v-if="suggestions.length"
            class="mrts__suggest">
            <span class="mrts__suggest-label">แท็กที่ใช้บ่อย</span>
            <button
              v-for="suggestion in suggestions"
              :key="suggestion"
              type="button"
              class="mrts__suggest-chip"
              @click="addTag(suggestion)">
              + {{ suggestion }}
            </button>
          </div>
        </div>

        <div class="mrts__sheet-foot">
          <div class="mrts__foot-left">
            <span class="mrts__summary">{{ summary }}</span>
            <button
              v-if="isEditing"
              type="button"
              class="mrts__link-danger"
              @click="deleteFromModal">
              ลบรายงานนี้
            </button>
          </div>
          <div class="mrts__foot-actions">
            <button
              type="button"
              class="mrts__btn mrts__btn--muted"
              @click="close">
              ยกเลิก
            </button>
            <button
              type="button"
              class="mrts__btn mrts__btn--primary"
              :disabled="saveDisabled || saving"
              @click="save">
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonthlyReportTagSettingProvider from '@/resources/MonthlyReportTagSettingProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'

const ReportService = new MonthlyReportTagSettingProvider()
const ProductAttributeService = new ProductAttributeProvider()

const MAX_TAGS = 12
const MAX_TAG_LENGTH = 24
const MAX_NAME_LENGTH = 60
const SEND_DAY = 7

export default {
  name: 'MonthlyReportTagSetting',
  data () {
    return {
      loading: true,
      saving: false,
      mutatingId: null,
      query: {
        page: 1,
        limit: 25,
        sortBy: ['updatedAt'],
        sortDesc: [true]
      },
      pagination: {
        total: 0,
        pages: 1
      },
      limitOptions: [10, 25, 50, 100],
      reports: [],
      stats: {
        total: 0,
        active: 0,
        tags: 0,
        nextSendDate: null
      },
      suggestionPool: [],
      productTags: [],
      isFetchingTags: false,
      toast: '',
      confirmingId: null,
      // modal state
      modalOpen: false,
      editingId: null,
      name: '',
      tags: [],
      draft: '',
      error: '',
      showDropdown: false,
      highlightIndex: -1
    }
  },
  computed: {
    isEditing () {
      return !!this.editingId
    },
    saveDisabled () {
      return !this.name.trim() || this.tags.length === 0
    },
    summary () {
      return this.tags.length
        ? `เลือกแล้ว ${this.tags.length} แท็ก (สูงสุด ${MAX_TAGS})`
        : 'ยังไม่ได้เลือกแท็ก'
    },
    suggestions () {
      return this.suggestionPool
        .filter((s) => !this.tags.some((t) => t.toLowerCase() === s.toLowerCase()))
        .slice(0, 4)
    },
    tagOptions () {
      const keyword = this.draft.trim().toLowerCase()
      const selected = new Set(this.tags.map((t) => t.toLowerCase()))
      return this.productTags
        .filter((name) => !selected.has(name.toLowerCase()))
        .filter((name) => !keyword || name.toLowerCase().includes(keyword))
        .slice(0, 8)
    },
    canAddCustom () {
      const value = this.draft.trim().toLowerCase()
      if (!value) return false
      if (this.tags.some((t) => t.toLowerCase() === value)) return false
      return !this.productTags.some((name) => name.toLowerCase() === value)
    },
    dropdownVisible () {
      return this.showDropdown && (this.tagOptions.length > 0 || this.canAddCustom)
    },
    nextSendLabel () {
      const today = this.$dayjs()
      const base = today.date() < SEND_DAY ? today : today.add(1, 'month')
      return this.formatThaiDate(base.date(SEND_DAY))
    },
    total () {
      return this.pagination.total
    },
    totalPages () {
      return this.pagination.pages
    },
    rangeStart () {
      return this.pagination.total ? (this.query.page - 1) * this.query.limit + 1 : 0
    },
    rangeEnd () {
      return Math.min(this.query.page * this.query.limit, this.pagination.total)
    }
  },
  watch: {
    'query.page': {
      handler () {
        this.fetchReports()
      },
      deep: true
    }
  },
  mounted () {
    this.reload()
    this.fetchSuggestions()
    this.getTags()
    window.addEventListener('keydown', this.onEscape)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onEscape)
  },
  methods: {
    reportId (report) {
      return report._id || report.id
    },
    isActive (report) {
      return report.status === 'active'
    },
    visibleTags (report) {
      return (report.tags || []).slice(0, 3)
    },
    moreCount (report) {
      const total = (report.tags || []).length
      return total > 3 ? total - 3 : 0
    },
    formatThaiDate (date) {
      if (!date) return '-'
      const d = this.$dayjs(date).locale('th')
      return `${d.format('D MMM')} ${d.year() + 543}`
    },
    prevPage () {
      if (this.query.page > 1) this.query.page -= 1
    },
    nextPage () {
      if (this.query.page < this.pagination.pages) this.query.page += 1
    },
    onLimitChange () {
      if (this.query.page === 1) {
        this.fetchReports()
      } else {
        this.query.page = 1
      }
    },
    notifyError (context, error) {
      console.error(context, error)
      this.$store.dispatch('Components/setSnackbar', {
        value: true,
        message: error?.message || error,
        type: 'error'
      })
    },
    reload () {
      this.fetchReports()
      this.fetchStats()
    },
    async fetchReports () {
      try {
        this.loading = true
        const { data } = await ReportService.getReports(this.query)
        this.reports = data.results || []
        this.pagination.total = data.total || 0
        this.pagination.pages = data.pages || 1
      } catch (error) {
        this.notifyError('fetchReports', error)
      } finally {
        this.loading = false
      }
    },
    async fetchStats () {
      try {
        const { data } = await ReportService.getStats()
        const stats = data || {}
        this.stats = {
          total: stats.statTotal || 0,
          active: stats.statActive || 0,
          tags: stats.statTags || 0,
          nextSendDate: stats.nextSendDate || null
        }
      } catch (error) {
        this.notifyError('fetchStats', error)
      }
    },
    async fetchSuggestions () {
      try {
        const { data } = await ReportService.getTagSuggestions()
        this.suggestionPool = data || []
      } catch (error) {
        // Suggestions are optional — fail silently but log.
        console.error('fetchSuggestions', error)
      }
    },
    async getTags () {
      try {
        this.isFetchingTags = true
        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999999,
          sortBy: ['id'],
          sortDesc: [true]
        })
        this.productTags = (data.results || []).map((tag) => tag.name).filter(Boolean)
      } catch (error) {
        // Product tags feed the autocomplete only — free typing still works if this fails.
        console.error('getTags', error)
      } finally {
        this.isFetchingTags = false
      }
    },
    // ----- Toggle -----
    async toggle (report) {
      const id = this.reportId(report)
      const nextStatus = this.isActive(report) ? 'inactive' : 'active'
      try {
        this.mutatingId = id
        this.confirmingId = null
        await ReportService.toggleReport(id, { status: nextStatus })
        this.toast = `${nextStatus === 'active' ? 'เปิดส่ง “' : 'หยุดส่ง “'}${report.name}” แล้ว`
        await this.fetchReports()
        this.fetchStats()
      } catch (error) {
        this.notifyError('toggle', error)
      } finally {
        this.mutatingId = null
      }
    },
    // ----- Delete -----
    askDelete (report) {
      this.confirmingId = this.reportId(report)
      this.toast = ''
    },
    cancelDelete () {
      this.confirmingId = null
    },
    async confirmDelete (report) {
      const id = this.reportId(report)
      try {
        this.mutatingId = id
        await ReportService.deleteReport(id)
        this.confirmingId = null
        if (this.editingId === id) {
          this.modalOpen = false
        }
        this.toast = `ลบรายงาน “${report.name}” แล้ว`
        await this.fetchReports()
        if (!this.reports.length && this.query.page > 1) {
          this.query.page -= 1
        }
        this.fetchStats()
      } catch (error) {
        this.notifyError('confirmDelete', error)
      } finally {
        this.mutatingId = null
      }
    },
    deleteFromModal () {
      const report = this.reports.find((r) => this.reportId(r) === this.editingId)
      if (report) this.confirmDelete(report)
    },
    // ----- Modal -----
    openCreate () {
      this.resetModal(null)
      this.modalOpen = true
      this.$nextTick(() => this.focusInput())
    },
    openEdit (report) {
      this.resetModal(report)
      this.modalOpen = true
      this.$nextTick(() => this.focusInput())
    },
    resetModal (report) {
      this.editingId = report ? this.reportId(report) : null
      this.name = report ? report.name : ''
      this.tags = report ? (report.tags || []).slice() : []
      this.draft = ''
      this.error = ''
      this.toast = ''
      this.showDropdown = false
      this.highlightIndex = -1
    },
    close () {
      this.modalOpen = false
      this.closeDropdown()
    },
    onEscape (event) {
      if (event.key === 'Escape' && this.modalOpen) {
        this.modalOpen = false
      }
    },
    focusInput () {
      const input = this.$refs.tagInput
      if (input) input.focus()
    },
    onNameInput () {
      if (this.name.length > MAX_NAME_LENGTH) {
        this.name = this.name.slice(0, MAX_NAME_LENGTH)
      }
    },
    addTag (raw) {
      const value = String(raw || '').trim().replace(/^#/, '')
      if (!value) return
      if (this.tags.length >= MAX_TAGS) {
        this.error = `เพิ่มแท็กได้สูงสุด ${MAX_TAGS} แท็ก`
        return
      }
      if (value.length > MAX_TAG_LENGTH) {
        this.error = `แท็กต้องยาวไม่เกิน ${MAX_TAG_LENGTH} ตัวอักษร`
        return
      }
      if (this.tags.some((t) => t.toLowerCase() === value.toLowerCase())) {
        this.error = `มีแท็ก “${value}” อยู่แล้ว`
        return
      }
      this.tags.push(value)
      this.draft = ''
      this.error = ''
      this.highlightIndex = -1
    },
    selectOption (name) {
      this.addTag(name)
      this.focusInput()
    },
    removeTag (index) {
      this.tags.splice(index, 1)
      this.error = ''
    },
    openDropdown () {
      this.showDropdown = true
      this.highlightIndex = -1
    },
    closeDropdown () {
      this.showDropdown = false
      this.highlightIndex = -1
    },
    onDraftInput () {
      this.error = ''
      this.showDropdown = true
      this.highlightIndex = -1
    },
    onKeyDown (event) {
      const options = this.tagOptions
      if (event.key === 'ArrowDown') {
        if (!this.dropdownVisible) this.showDropdown = true
        event.preventDefault()
        if (options.length) {
          this.highlightIndex = (this.highlightIndex + 1) % options.length
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (options.length) {
          this.highlightIndex = this.highlightIndex <= 0 ? options.length - 1 : this.highlightIndex - 1
        }
      } else if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
        if (this.highlightIndex >= 0 && options[this.highlightIndex]) {
          event.preventDefault()
          this.addTag(options[this.highlightIndex])
        } else if (this.draft.trim()) {
          event.preventDefault()
          this.addTag(this.draft)
        }
      } else if (event.key === 'Escape' && this.dropdownVisible) {
        event.stopPropagation()
        this.closeDropdown()
      } else if (event.key === 'Backspace' && !this.draft && this.tags.length) {
        this.tags.pop()
        this.error = ''
      }
    },
    async save () {
      const name = this.name.trim()
      if (!name || !this.tags.length) return
      try {
        this.saving = true
        const payload = { name, tags: this.tags.slice() }
        if (this.editingId) {
          await ReportService.updateReport(this.editingId, payload)
          this.toast = `อัปเดตรายงาน “${name}” แล้ว`
        } else {
          await ReportService.createReport({ ...payload, status: 'active' })
          this.toast = `สร้างรายงาน “${name}” แล้ว`
        }
        this.modalOpen = false
        if (!this.editingId && this.query.page !== 1) {
          this.query.page = 1
        } else {
          await this.fetchReports()
        }
        this.fetchStats()
        this.fetchSuggestions()
      } catch (error) {
        this.error = error?.message || 'บันทึกไม่สำเร็จ'
        this.notifyError('save', error)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

.mrts {
  height: calc(100vh - 96px);
  overflow: hidden;
  background: #F5F3EE;
  color: #1A1A18;
  font-family: 'IBM Plex Sans Thai', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.mrts__container {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 40px 32px 48px;
  box-sizing: border-box;
  overflow-y: auto;
}

/* Header */
.mrts__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.mrts__eyebrow {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8A8375;
}

.mrts__title {
  font-size: 30px;
  line-height: 1.25;
  font-weight: 600;
  margin: 8px 0;
  letter-spacing: -0.01em;
}

.mrts__subtitle {
  font-size: 14.5px;
  line-height: 1.6;
  color: #5F5A50;
  margin: 0;
  max-width: 65ch;
}

/* Buttons */
.mrts__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}

.mrts__btn--primary {
  padding: 11px 20px;
  border: 1px solid #1F5F4B;
  border-radius: 8px;
  background: #1F5F4B;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;
}

.mrts__btn--primary:hover:not(:disabled) {
  background: #17493A;
  border-color: #17493A;
}

.mrts__btn--primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.mrts__btn--ghost {
  height: 32px;
  box-sizing: border-box;
  padding: 0 13px;
  border: 1px solid #DFDACF;
  border-radius: 7px;
  background: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
  color: #5F5A50;
}

.mrts__btn--ghost:hover {
  border-color: #1F5F4B;
  color: #1F5F4B;
}

.mrts__btn--muted {
  padding: 10px 18px;
  border: 1px solid #DFDACF;
  border-radius: 8px;
  background: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  color: #5F5A50;
}

.mrts__btn--muted:hover {
  border-color: #C3BCAD;
  color: #1A1A18;
}

.mrts__btn--danger {
  padding: 7px 16px;
  border: 1px solid #B0492F;
  border-radius: 7px;
  background: #B0492F;
  font-size: 13px;
  font-weight: 600;
  color: #FFFFFF;
}

.mrts__btn--danger:hover:not(:disabled) {
  background: #933A23;
  border-color: #933A23;
}

/* Stat cards */
.mrts__stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.mrts__stat {
  flex: 1 1 200px;
  background: #FFFFFF;
  border: 1px solid #E4E0D7;
  border-radius: 12px;
  padding: 16px 18px;
}

.mrts__stat-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8A8375;
  margin-bottom: 8px;
}

.mrts__stat-value {
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.mrts__stat-value--green {
  color: #1F5F4B;
}

.mrts__stat-value--date {
  font-size: 18px;
  padding-top: 6px;
}

/* Table */
.mrts__table {
  background: #FFFFFF;
  border: 1px solid #E4E0D7;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(26, 26, 24, 0.04);
}

.mrts__row {
  display: grid;
  grid-template-columns: minmax(220px, 1.1fr) minmax(240px, 1.4fr) 130px 92px;
  gap: 20px;
  padding: 18px 24px;
}

.mrts__row--head {
  align-items: center;
  padding: 13px 24px;
  background: #FBFAF7;
  border-bottom: 1px solid #EFEBE2;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8A8375;
}

.mrts__row--item {
  align-items: center;
  border-bottom: 1px solid #F1EDE5;
  transition: background 0.16s ease;
}

.mrts__row--item:hover {
  background: #FCFBF8;
}

.mrts__col-right {
  text-align: right;
}

/* Pager */
.mrts__pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 24px;
  background: #FBFAF7;
  font-size: 13px;
  color: #5F5A50;
}

.mrts__pager--top {
  border-bottom: 1px solid #EFEBE2;
}

.mrts__pager--bottom {
  border-top: 1px solid #EFEBE2;
}

.mrts__pager-rows {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mrts__pager-select {
  font-family: inherit;
  font-size: 13px;
  color: #1A1A18;
  padding: 5px 8px;
  background: #FFFFFF;
  border: 1px solid #DFDACF;
  border-radius: 7px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.16s ease;
}

.mrts__pager-select:focus {
  border-color: #1F5F4B;
}

.mrts__pager-range {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12.5px;
  color: #5F5A50;
  min-width: 120px;
  text-align: right;
}

.mrts__pager-nav {
  display: flex;
  gap: 6px;
}

.mrts__pager-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #DFDACF;
  border-radius: 7px;
  background: #FFFFFF;
  color: #5F5A50;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease;
}

.mrts__pager-btn:hover:not(:disabled) {
  border-color: #1F5F4B;
  color: #1F5F4B;
  background: #F2F7F4;
}

.mrts__pager-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.mrts__name {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.mrts__meta {
  font-size: 12.5px;
  color: #8A8375;
}

/* Chips */
.mrts__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mrts__chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #EAF1ED;
  border: 1px solid #CFE0D7;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 500;
  color: #1F5F4B;
}

.mrts__chip--more {
  background: transparent;
  border: 1px dashed #D6D0C4;
  color: #7A7367;
}

/* Switch */
.mrts__switch {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  color: #8A8375;
}

.mrts__switch:disabled {
  cursor: default;
}

.mrts__switch--on {
  color: #1F5F4B;
}

.mrts__switch-track {
  position: relative;
  width: 34px;
  height: 20px;
  flex: none;
  border-radius: 999px;
  background: #D6D0C4;
  transition: background 0.16s ease;
}

.mrts__switch--on .mrts__switch-track {
  background: #1F5F4B;
}

.mrts__switch-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #FFFFFF;
  box-shadow: 0 1px 2px rgba(26, 26, 24, 0.28);
  transition: left 0.16s ease;
}

.mrts__switch--on .mrts__switch-knob {
  left: 16px;
}

/* Actions */
.mrts__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.mrts__icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #DFDACF;
  border-radius: 7px;
  background: #FFFFFF;
  color: #8A8375;
  cursor: pointer;
  padding: 0;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease;
}

.mrts__icon-btn:hover {
  border-color: #B0492F;
  color: #B0492F;
  background: #FDF5F3;
}

/* Inline delete confirm */
.mrts__confirm {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 16px;
  background: #FDF5F3;
  border: 1px solid #EBD3CB;
  border-radius: 10px;
}

.mrts__confirm-text {
  font-size: 13px;
  color: #8C3A24;
}

.mrts__confirm-actions {
  display: flex;
  gap: 8px;
}

/* States */
.mrts__state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 24px;
}

.mrts__empty {
  padding: 64px 24px;
  text-align: center;
}

.mrts__empty-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}

.mrts__empty-text {
  font-size: 13.5px;
  color: #7A7367;
  margin: 0 0 18px;
}

/* Toast */
.mrts__toast {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #EAF1ED;
  border: 1px solid #CFE0D7;
  border-radius: 10px;
  font-size: 13.5px;
  color: #1F5F4B;
}

.mrts__toast-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #1F5F4B;
  flex: none;
}

/* Modal */
.mrts__veil {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: rgba(26, 26, 24, 0.42);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  box-sizing: border-box;
  animation: mrtsVeilIn 0.14s ease-out;
}

.mrts__sheet {
  width: 100%;
  max-width: 720px;
  max-height: 100%;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #E4E0D7;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(26, 26, 24, 0.22);
  animation: mrtsSheetIn 0.18s cubic-bezier(0.2, 0.8, 0.3, 1);
}

.mrts__sheet-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #EFEBE2;
}

.mrts__sheet-title {
  font-size: 19px;
  font-weight: 600;
  margin: 0 0 5px;
  letter-spacing: -0.01em;
}

.mrts__sheet-sub {
  font-size: 13px;
  color: #7A7367;
  margin: 0;
}

.mrts__close {
  flex: none;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E4E0D7;
  border-radius: 8px;
  background: #FFFFFF;
  font-size: 15px;
  color: #7A7367;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.16s ease, color 0.16s ease;
}

.mrts__close:hover {
  background: #F5F3EE;
  color: #1A1A18;
}

.mrts__field {
  padding: 22px 28px;
}

.mrts__field--tags {
  padding: 4px 28px 24px;
}

.mrts__label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
}

.mrts__hint {
  font-size: 12.5px;
  color: #7A7367;
  margin: 0 0 10px;
}

.mrts__input {
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 15px;
  color: #1A1A18;
  padding: 11px 14px;
  background: #FBFAF7;
  border: 1px solid #DFDACF;
  border-radius: 8px;
  outline: none;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.mrts__input:focus {
  border-color: #1F5F4B;
  background: #FFFFFF;
  box-shadow: 0 0 0 3px rgba(31, 95, 75, 0.12);
}

.mrts__input::placeholder {
  color: #A8A296;
}

.mrts__counter {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: #A8A296;
}

/* Tag input */
.mrts__tagfield {
  position: relative;
}

.mrts__dropdown {
  margin-top: 8px;
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
  background: #FFFFFF;
  border: 1px solid #E4E0D7;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(26, 26, 24, 0.14);
  animation: mrtsSheetIn 0.14s ease-out;
}

.mrts__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  border: none;
  border-radius: 7px;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  color: #1A1A18;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.mrts__option--active {
  background: #F2F7F4;
  color: #1F5F4B;
}

.mrts__option-dot {
  width: 6px;
  height: 6px;
  flex: none;
  border-radius: 999px;
  background: #CFE0D7;
}

.mrts__option--active .mrts__option-dot {
  background: #1F5F4B;
}

.mrts__option--add {
  font-size: 13px;
  color: #5F5A50;
  font-weight: 500;
  border-top: 1px solid #F1EDE5;
  margin-top: 2px;
  border-radius: 0 0 7px 7px;
}

.mrts__option--add:hover {
  background: #F2F7F4;
  color: #1F5F4B;
}

.mrts__tagbox {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-height: 48px;
  padding: 9px 10px;
  background: #FBFAF7;
  border: 1px solid #DFDACF;
  border-radius: 8px;
  cursor: text;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.mrts__tagbox:focus-within {
  border-color: #1F5F4B;
  background: #FFFFFF;
  box-shadow: 0 0 0 3px rgba(31, 95, 75, 0.12);
}

.mrts__tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 6px 5px 11px;
  background: #EAF1ED;
  border: 1px solid #CFE0D7;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #1F5F4B;
  animation: mrtsChipIn 0.16s ease-out;
}

.mrts__tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #4C7A68;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: background 0.16s ease, color 0.16s ease;
}

.mrts__tag-remove:hover {
  background: #1F5F4B;
  color: #FFFFFF;
}

.mrts__tag-input {
  flex: 1 1 140px;
  min-width: 140px;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  color: #1A1A18;
  padding: 5px 4px;
}

.mrts__tag-input::placeholder {
  color: #A8A296;
}

.mrts__error {
  min-height: 20px;
  margin-top: 8px;
  font-size: 12.5px;
  color: #B0492F;
}

.mrts__suggest {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.mrts__suggest-label {
  font-size: 12.5px;
  color: #8A8375;
  margin-right: 2px;
}

.mrts__suggest-chip {
  padding: 5px 11px;
  border: 1px dashed #D6D0C4;
  border-radius: 999px;
  background: transparent;
  font-family: inherit;
  font-size: 12.5px;
  color: #5F5A50;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease;
}

.mrts__suggest-chip:hover {
  border-color: #1F5F4B;
  border-style: solid;
  color: #1F5F4B;
  background: #F2F7F4;
}

.mrts__sheet-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 28px;
  background: #FBFAF7;
  border-top: 1px solid #EFEBE2;
  border-radius: 0 0 16px 16px;
}

.mrts__foot-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mrts__summary {
  font-size: 13px;
  color: #7A7367;
}

.mrts__link-danger {
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: #B0492F;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.mrts__link-danger:hover {
  color: #8C3A24;
}

.mrts__foot-actions {
  display: flex;
  gap: 10px;
}

@keyframes mrtsChipIn {
  from { opacity: 0; transform: translateY(4px) scale(0.96); }
  to { opacity: 1; transform: none; }
}

@keyframes mrtsSheetIn {
  from { opacity: 0; transform: translateY(14px) scale(0.985); }
  to { opacity: 1; transform: none; }
}

@keyframes mrtsVeilIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
